import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

const DispositionSettings = ({ open, onClose, onSave, currentSettings = [], availableDispositions = [] }) => {
  const [selectedDispositions, setSelectedDispositions] = useState(currentSettings);

  // Create disposition options from available dispositions or use defaults
  const DISPOSITION_OPTIONS = availableDispositions.length > 0 
    ? availableDispositions.map(dispo => ({
        value: dispo,
        label: dispo.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
        color: getDispositionColor(dispo)
      }))
    : [
        { value: 'SALE', label: 'Sale', color: 'success' },
        { value: 'QUALIFIED', label: 'Qualified', color: 'primary' },
        { value: 'FOLLOW_UP', label: 'Follow Up', color: 'warning' },
        { value: 'XFER', label: 'Transfer', color: 'info' },
        { value: 'CALLBACK', label: 'Callback', color: 'warning' },
        { value: 'NO_SALE', label: 'No Sale', color: 'error' },
        { value: 'NOT_INTERESTED', label: 'Not Interested', color: 'default' },
        { value: 'NI', label: 'Not Interested', color: 'default' },
        { value: 'WRONG_NUMBER', label: 'Wrong Number', color: 'secondary' },
        { value: 'NO_ANSWER', label: 'No Answer', color: 'info' },
        { value: 'BUSY', label: 'Busy', color: 'secondary' },
        { value: 'VOICEMAIL', label: 'Voicemail', color: 'info' },
        { value: 'VM', label: 'Voicemail', color: 'info' },
        { value: 'DNC', label: 'Do Not Call', color: 'error' },
        { value: 'DROP', label: 'Dropped Call', color: 'secondary' },
        { value: 'HANGUP', label: 'Hangup', color: 'secondary' },
        { value: 'ANSWERING_MACHINE', label: 'Answering Machine', color: 'info' },
        { value: 'AM', label: 'Answering Machine', color: 'info' }
      ];

  // Helper function to get disposition color
  function getDispositionColor(disposition) {
    const colorMap = {
      'SALE': 'success',
      'QUALIFIED': 'primary', 
      'FOLLOW_UP': 'warning',
      'XFER': 'info',
      'CALLBACK': 'warning',
      'NO_SALE': 'error',
      'NOT_INTERESTED': 'default',
      'NI': 'default',
      'WRONG_NUMBER': 'secondary',
      'NO_ANSWER': 'info',
      'BUSY': 'secondary',
      'VOICEMAIL': 'info',
      'VM': 'info',
      'DNC': 'error',
      'DROP': 'secondary',
      'HANGUP': 'secondary',
      'ANSWERING_MACHINE': 'info',
      'AM': 'info'
    };
    return colorMap[disposition] || 'default';
  }

  useEffect(() => {
    setSelectedDispositions(currentSettings);
  }, [currentSettings]);

  const handleDispositionChange = (disposition, checked) => {
    if (checked) {
      setSelectedDispositions(prev => [...prev, disposition]);
    } else {
      setSelectedDispositions(prev => prev.filter(d => d !== disposition));
    }
  };

  const handleSave = () => {
    onSave(selectedDispositions);
    onClose();
  };

  const handleReset = () => {
    setSelectedDispositions(['SALE', 'QUALIFIED', 'FOLLOW_UP']); // Default VICIdial disposition settings
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsIcon />
          <Typography variant="h6">
            Call Analysis Auto-Trigger Settings
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">
            Select which dispositions should automatically trigger call analysis and transcription.
            This helps ensure important calls are processed for quality assurance.
          </Alert>
        </Box>

        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, mb: 1 }}>
          Select Dispositions for Auto Call Analysis:
        </Typography>

        <FormGroup>
          {DISPOSITION_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={selectedDispositions.includes(option.value)}
                  onChange={(e) => handleDispositionChange(option.value, e.target.checked)}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={option.label} 
                    color={option.color} 
                    size="small" 
                  />
                  <Typography variant="body2">
                    {option.label}
                  </Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Currently Selected ({selectedDispositions.length}):
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedDispositions.map(disposition => {
              const option = DISPOSITION_OPTIONS.find(opt => opt.value === disposition);
              return (
                <Chip
                  key={disposition}
                  label={option?.label || disposition}
                  color={option?.color || 'default'}
                  size="small"
                />
              );
            })}
            {selectedDispositions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No dispositions selected for auto-trigger
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} color="secondary">
          Reset to Default
        </Button>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DispositionSettings; 